// ─── Relances — write one in a sentence, read it, then arm it ────────────────
//
// Three deliberate steps, and the separation is the safety:
//
//   1. WRITE   the owner types a sentence; the model proposes a trigger, a
//              delay and a message.
//   2. READ    the proposal is shown as an editable form. Nothing is saved
//              until they press save, and it saves DISABLED.
//   3. ARM     a separate switch per rule. Until it is on, nothing sends.
//
// ⚠️ THE SWITCH IS THE LOUDEST THING ON EACH CARD. This is the only screen in
// the admin that can start messaging many customers at once, so the state of
// each rule has to be readable at a glance and never inferred.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { followUpsApi, type FollowUpRule, type DraftedRule, type FollowUpStat } from '@/lib/api';
import { approvalPlan, approveAllLabel, approveAllConfirm, QUEUE_TEXT } from './followUpQueuePlan';
import { Card, CardHeader, EmptyState } from './_ui';
import { useT } from '@/lib/i18n';

const inputCls =
  'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white transition-colors duration-200 ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent';

/** The same switch shape as the master switch on Pilotage, so "on" reads the
 *  same everywhere in this area. */
function Switch({ on, onChange, disabled, label }: {
  on: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string;
}) {
  return (
    <button
      type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
      onClick={() => onChange(!on)}
      className={`relative w-[52px] h-[30px] rounded-full transition-colors duration-300 shrink-0 cursor-pointer
        disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500
        ${on ? 'bg-green-600' : 'bg-gray-300'}`}
    >
      <span
        aria-hidden="true"
        className={`absolute left-0 top-[3px] w-6 h-6 rounded-full bg-white shadow-sm transition-transform
          duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${on ? 'translate-x-[25px]' : 'translate-x-[3px]'}`}
      />
    </button>
  );
}

/**
 * Wording for the hard-coded ladders, keyed by the `follow_up_history`
 * sub_state they write.
 *
 * ⚠️ THE WORKER DECIDES WHICH LADDERS EXIST; this only names them. Keeping the
 * list here too would let the page quietly drop one. A bucket with no entry
 * renders nothing rather than an untitled card — visible in review, harmless
 * to a customer.
 */
const BUILT_IN_COPY: Record<string, { name: string; when: string }> = {
  photos_missing: {
    name: 'Demande de photos (intégrée)',
    when: 'Après la commande, puis à 24 h et 72 h — sauf si le client a écrit en premier.',
  },
  cover_not_chosen: {
    name: 'Couverture non choisie (intégrée)',
    when: 'À 24 h et 48 h après l’envoi du lien — avec son lien dedans. Rien n’est envoyé si le lien n’est jamais parti.',
  },
  awaiting_preview_feedback: {
    name: 'Aperçu sans réponse (intégrée)',
    when: 'À 24 h et 48 h après l’envoi de l’aperçu. Rien n’est envoyé sans preuve que le client l’a bien reçu.',
  },
  awaiting_payment: {
    name: 'Validé, pas payé (intégrée)',
    when: 'À 24 h et 72 h après la demande de paiement, avec votre RIB enregistré. Les commandes payées à la livraison sont relancées aussi, pour l’acompte ; un paiement reçu arrête tout.',
  },
};

/** A stable object, so a rule the stats query has not answered for renders
 *  explicit zeros instead of flickering a new identity on every render. */
const EMPTY_STAT: FollowUpStat = {
  sent: 0, replied: 0, advanced: 0, advancedOf: 0, lastSentAt: null,
  replyRate: null, advancedRate: null, verdict: 'unproven',
};

/* ── Did it work? ────────────────────────────────────────────────────────────
 *
 * ⚠️ THREE NUMBERS, AND ONLY ONE OF THEM IS PROOF. "Replied" is unambiguous —
 * the customer read the message and answered it. "Moved on" only says the
 * order advanced within two weeks of the send; it cannot see whether the
 * message caused that, so it is never called a conversion and never gets the
 * visual weight of the reply figure.
 *
 * ⚠️ NO RATE BELOW FIVE SENDS. One reply out of two is not 50%, it is one
 * reply. The whole point of this strip is deciding whether to keep a rule, and
 * a percentage printed over two data points invites exactly the wrong call.
 */

/** A verdict badge. Word first — colour is never the only carrier of meaning. */
function Verdict({ v }: { v: FollowUpStat['verdict'] }) {
  const { t } = useT();
  const map: Record<FollowUpStat['verdict'], { label: string; cls: string }> = {
    working:  { label: t('Marche bien'),      cls: 'bg-green-50 text-green-700 border-green-200' },
    weak:     { label: t('Peu de réponses'),  cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    dead:     { label: t('Personne ne répond'), cls: 'bg-red-50 text-red-700 border-red-200' },
    unproven: { label: t('Pas assez de recul'), cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  };
  const m = map[v];
  return (
    <span className={`text-[10.5px] font-semibold px-2 py-[3px] rounded-full border ${m.cls}`}>
      {m.label}
    </span>
  );
}

function Figure({ n, label, hint, strong, title }: {
  n: number; label: string; hint?: string; strong?: boolean; title?: string;
}) {
  return (
    <div className="min-w-0" title={title}>
      <p className={`tabular-nums leading-none ${
        strong ? 'text-[19px] font-bold text-gray-900' : 'text-[17px] font-semibold text-gray-700'
      }`}>{n}</p>
      <p className="text-[10.5px] text-gray-500 mt-1 leading-tight">{label}</p>
      {hint && <p className="text-[10px] text-gray-400 leading-tight">{hint}</p>}
    </div>
  );
}

function RuleStats({ stat, windows }: {
  stat: FollowUpStat; windows: { replyHours: number; attributionDays: number };
}) {
  const { t } = useT();

  // ⚠️ NEVER FIRED IS NOT A RESULT. A row of zeros reads as failure; say
  // plainly that there is nothing to judge yet.
  if (stat.sent === 0) {
    return (
      <p className="text-[11.5px] text-gray-400 mt-3 pt-3 border-t border-gray-100">
        {t('Jamais envoyée — rien à mesurer pour l’instant.')}
      </p>
    );
  }

  const pct = stat.replyRate === null ? null : Math.round(stat.replyRate * 100);

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div className="flex items-start gap-5 flex-wrap">
        <Figure n={stat.sent} label={t('envoyées')} />
        <Figure
          strong
          n={stat.replied}
          label={t('ont répondu')}
          hint={pct === null
            ? t('trop peu pour un %')
            : t('{p} % · sous {h} h', { p: String(pct), h: String(windows.replyHours) })}
        />
        <Figure
          n={stat.advanced}
          label={t('ont avancé')}
          // The denominator is stated because it is NOT the number sent —
          // sends with no recorded status cannot be judged either way.
          hint={stat.advancedOf === stat.sent
            ? t('sous {d} j', { d: String(windows.attributionDays) })
            : t('sur {n} mesurables', { n: String(stat.advancedOf) })}
          // The caveat itself is stated ONCE under the section heading. It was
          // on every card first: five identical copies of the same two lines,
          // which is how a warning becomes wallpaper. Here it stays reachable
          // on the figure it qualifies without being read five times.
          title={t('La commande a bougé dans les {d} jours suivant le message. Cela ne prouve pas que le message en soit la cause.',
            { d: String(windows.attributionDays) })}
        />
        <span className="ml-auto self-center"><Verdict v={stat.verdict} /></span>
      </div>

      {pct !== null && (
        <span className="mt-2.5 block h-[6px] bg-gray-100 rounded-full overflow-hidden">
          <span
            className={`block h-full rounded-full transition-[width] duration-700 ease-out ${
              stat.verdict === 'working' ? 'bg-green-500'
                : stat.verdict === 'dead' ? 'bg-red-400' : 'bg-amber-400'
            }`}
            style={{ width: `${Math.max(2, pct)}%` }}
          />
        </span>
      )}
    </div>
  );
}

function RuleCard({ rule, laneLabels, stat, windows }: {
  rule: FollowUpRule; laneLabels: Record<string, string>;
  stat: FollowUpStat; windows: { replyHours: number; attributionDays: number };
}) {
  const { t } = useT();
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['followup-rules'] });

  const patch = useMutation({
    mutationFn: (p: Record<string, unknown>) => followUpsApi.updateRule(rule.id, p),
    onSuccess: () => { setMsg(null); invalidate(); },
  });
  const del = useMutation({
    mutationFn: () => followUpsApi.deleteRule(rule.id),
    onSuccess: invalidate,
  });

  return (
    <div className={`rounded-xl border p-4 transition-colors duration-300 ${
      rule.enabled ? 'border-green-200 bg-green-50/40' : 'border-gray-200 bg-white'
    }`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-gray-900">{rule.name}</p>
          <p className="text-[11.5px] text-gray-600 mt-0.5">
            {t('Quand : {lane} depuis {h} h', {
              lane: laneLabels[rule.lane] ?? rule.lane, h: String(rule.after_hours),
            })}
            {rule.requires_approval
              ? ` · ${t('demande votre accord')}`
              : ` · ${t('part tout seul')}`}
          </p>
        </div>
        <Switch
          on={rule.enabled}
          disabled={patch.isPending}
          label={t('Activer cette relance')}
          onChange={(v) => {
            if (v && !rule.requires_approval && !window.confirm(t(
              'Activer cette relance en envoi automatique ?\n\nElle partira toute seule, sans que vous la relisiez à chaque fois.',
            ))) return;
            patch.mutate({ enabled: v });
          }}
        />
      </div>

      <textarea
        className={`${inputCls} min-h-[74px] mt-3 text-[12.5px]`}
        value={msg ?? rule.message}
        onChange={(e) => setMsg(e.target.value)}
      />
      {patch.isError && (
        <p className="text-[11.5px] text-red-600 mt-1.5" role="alert">{(patch.error as Error).message}</p>
      )}

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {msg !== null && msg !== rule.message && (
          <Button size="sm" loading={patch.isPending} onClick={() => patch.mutate({ message: msg })}>
            {t('Enregistrer le message')}
          </Button>
        )}
        <label className="flex items-center gap-1.5 text-[11.5px] text-gray-600 cursor-pointer">
          <input
            type="checkbox" className="accent-brand-600"
            checked={rule.requires_approval}
            onChange={(e) => patch.mutate({ requires_approval: e.target.checked })}
          />
          {t('Me demander avant chaque envoi')}
        </label>
        <button
          className="text-[11.5px] text-red-600 underline ml-auto cursor-pointer"
          onClick={() => { if (window.confirm(t('Supprimer cette relance ?'))) del.mutate(); }}
        >{t('Supprimer')}</button>
      </div>

      {rule.source_text && (
        <p className="text-[11px] text-gray-400 mt-2 italic">
          {t('Votre demande : « {x} »', { x: rule.source_text })}
        </p>
      )}

      <RuleStats stat={stat} windows={windows} />
    </div>
  );
}

export function FollowUpsTab() {
  const { t } = useT();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [draft, setDraft] = useState<DraftedRule | null>(null);

  const rules = useQuery({ queryKey: ['followup-rules'], queryFn: () => followUpsApi.rules() });
  const queue = useQuery({
    queryKey: ['followup-queue'], queryFn: () => followUpsApi.queue(), refetchInterval: 60_000,
  });

  const drafting = useMutation({
    mutationFn: () => followUpsApi.draft(text),
    onSuccess: (r) => setDraft(r.rule),
  });
  const save = useMutation({
    mutationFn: (r: DraftedRule) => followUpsApi.createRule(r),
    onSuccess: () => {
      setDraft(null); setText('');
      qc.invalidateQueries({ queryKey: ['followup-rules'] });
    },
  });
  const decide = useMutation({
    mutationFn: ({ id, ok }: { id: string; ok: boolean }) => followUpsApi.decide(id, ok),
    // Settled, not only succeeded: a row older than 48 h answers 410 and is
    // dropped server-side — the list must still lose it now (2026-09-29).
    onSettled: () => qc.invalidateQueries({ queryKey: ['followup-queue'] }),
  });

  const laneLabels = rules.data?.laneLabels ?? {};
  const lanes = Object.entries(laneLabels);
  const list = rules.data?.rules ?? [];
  const pending = queue.data?.queue ?? [];
  /** Night in Morocco: approvals wait at the door until 08:00 (worker night hold). */
  const night = queue.data?.night ?? null;

  /** One entry per rule, in the order the rules first appear in the queue —
   *  so the grouping never reshuffles under the reader while they decide. */
  type QueueRow = (typeof pending)[number];
  const byRule: Array<[string, QueueRow[]]> = [];
  for (const q of pending) {
    const name = q.follow_up_rules?.name ?? 'Relance';
    const found = byRule.find(([n]) => n === name);
    if (found) found[1].push(q);
    else byRule.push([name, [q]]);
  }
  // Defaults only until the first load lands. The worker is the authority on
  // both windows — hard-coding different ones here would caption the numbers
  // with a rule the counting did not use.
  const windows = rules.data?.windows ?? { replyHours: 72, attributionDays: 14 };

  return (
    <div className="space-y-4">

      {/* ── 1 · Write it ── */}
      <Card>
        <h3 className="text-[13px] font-semibold text-gray-800">{t('Créer une relance')}</h3>
        <p className="text-[11.5px] text-gray-500 mt-0.5 mb-3 leading-snug">
          {t('Écrivez-la comme vous l’expliqueriez à quelqu’un. Exemple : « deux jours après avoir envoyé le lien de couverture, si le client n’a pas choisi, relance-le gentiment ».')}
        </p>
        <textarea
          className={`${inputCls} min-h-[76px]`} value={text}
          onChange={(e) => { setText(e.target.value); setDraft(null); }}
          placeholder={t('Décrivez la relance…')}
        />
        <div className="flex items-center gap-2 mt-2">
          <Button size="sm" loading={drafting.isPending} disabled={text.trim().length < 8}
            onClick={() => drafting.mutate()}>
            {t('Proposer la relance')}
          </Button>
          <span className="text-[11px] text-gray-400">{t('Rien n’est enregistré à cette étape.')}</span>
        </div>
        {drafting.isError && (
          <p className="text-[11.5px] text-red-600 mt-2" role="alert">{(drafting.error as Error).message}</p>
        )}
      </Card>

      {/* ── 2 · Read it ── */}
      {draft && (
        <Card className="border-brand-300 bg-brand-50/40 storiz-pop-in">
          <h3 className="text-[13px] font-semibold text-gray-800 mb-2">{t('Proposition — relisez avant d’enregistrer')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_170px_110px] gap-2">
            <input className={inputCls} value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <select className={inputCls} value={draft.lane}
              onChange={(e) => setDraft({ ...draft, lane: e.target.value })}>
              {lanes.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
            </select>
            <input className={inputCls} type="number" min={0} max={336} value={draft.after_hours}
              onChange={(e) => setDraft({ ...draft, after_hours: Number(e.target.value) })} />
          </div>
          <textarea className={`${inputCls} min-h-[90px] mt-2 text-[12.5px]`} value={draft.message}
            onChange={(e) => setDraft({ ...draft, message: e.target.value })} />
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Button size="sm" loading={save.isPending} onClick={() => save.mutate(draft)}>
              {t('Enregistrer (désactivée)')}
            </Button>
            <button className="text-[11.5px] text-gray-500 underline cursor-pointer" onClick={() => setDraft(null)}>
              {t('Annuler')}
            </button>
            <span className="text-[11px] text-gray-500">
              {t('Elle sera enregistrée éteinte — vous l’activerez vous-même.')}
            </span>
          </div>
          {save.isError && (
            <p className="text-[11.5px] text-red-600 mt-2" role="alert">{(save.error as Error).message}</p>
          )}
        </Card>
      )}

      {/* ── The approval queue ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <CardHeader
          title={t('En attente de votre accord')}
          sub={t('Rien ne part tant que vous n’avez pas dit oui.')}
          right={pending.length}
        />
        {!pending.length && <EmptyState>{t('Aucune relance en attente.')}</EmptyState>}
        {/* 2026-09-29 — approving at night is allowed; the messages wait until
            08:00 Morocco time. Said before the click, not discovered after. */}
        {night?.active && pending.length > 0 && (
          <p className="text-[11px] text-indigo-800 bg-indigo-50 border-b border-indigo-100 px-4 py-1.5">
            {t(QUEUE_TEXT.nightBanner)}
          </p>
        )}

        {/* ── GROUPED BY RULE, WITH THE WARNING SAID ONCE ────────────────────
            Eight cards, identical message, identical amber warning under each
            one. Repeating a warning eight times does not make it eight times
            louder — it makes it wallpaper, and the eight decisions it belongs
            to become one decision made eight times.

            ⚠️ « Tout approuver » APPROVES ONLY THE ROWS IN ITS OWN GROUP, and
            only after a confirm that names the count. It is the same per-row
            mutation in a loop, not a new endpoint — every gate each individual
            approval passes through is unchanged. ── */}
        {byRule.map(([ruleName, rows]) => {
          // ⚠️ WHAT THE CLICK WILL REALLY DO (2026-09-29): rows older than 48 h
          // are dropped by the worker, never sent; of the rest, numbers off the
          // test list do not go. Counted here, said on the button and in the
          // confirm — the 70 rows of 28 Sept went out on a bare « Tout approuver ».
          const plan = approvalPlan(rows);
          const unreachable = plan.unreachable;
          return (
            <div key={ruleName} className="border-b border-gray-100 last:border-b-0">
              <div className="px-4 py-2.5 bg-gray-50/70 flex items-center gap-3 flex-wrap">
                <span className="text-[12px] font-semibold text-gray-800">{ruleName}</span>
                <span className="text-[11px] text-gray-400 tabular-nums">{rows.length}</span>
                <Button
                  size="sm" loading={decide.isPending}
                  onClick={() => {
                    // ⚠️ THE CONFIRM NAMES WHAT WILL REALLY HAPPEN. « Envoyer les
                    // 27 relances ? » over a list where 25 are off the allowlist
                    // is a trap: approving writes 27 drafts, the serve pass
                    // refuses 25 in silence, and 2 arrive. Say the split first —
                    // and how many are too old to go at all, and, at night, that
                    // the rest wait for 08:00.
                    if (!window.confirm(approveAllConfirm(t, ruleName, plan, night))) return;
                    for (const r of rows) decide.mutate({ id: r.id, ok: true });
                  }}
                  className="ml-auto"
                >
                  {approveAllLabel(t, plan)}
                </Button>
              </div>
              {/* The warning, once per group rather than once per card. */}
              {unreachable > 0 && (
                <p className="text-[11px] text-amber-800 bg-amber-50 border-y border-amber-200 px-4 py-1.5">
                  {t('{n} de ces numéros ne sont pas dans la liste de test — approuver ne les enverra pas. Ajoutez-les dans Numéros.')
                    .replace('{n}', String(unreachable))}
                </p>
              )}
              {rows.map((q) => (
          <div key={q.id} className="px-4 py-3 border-b border-gray-100 last:border-b-0">
            <p className="text-[12px] font-semibold text-gray-800">
              {/* ⚠️ A COLD LEAD HAS NO ORDER, so the order join is null and
                  this used to read a bare "Client". A `@lid` chat id contains
                  no phone either — the worker resolves the real number from
                  the newest inbound message. You cannot approve a message
                  without knowing who receives it. */}
              {q.orders?.customer_name || q.contact_name || q.orders?.shopify_order_name
                || q.contact_phone || t('Client')}
              {!q.order_id && (
                <span className="ml-1.5 text-[10px] font-normal text-gray-400 align-middle">
                  {t('pas encore de commande')}
                </span>
              )}
              <span className="font-normal text-gray-400"> · {q.follow_up_rules?.name}</span>
            </p>
            <p className="text-[12.5px] text-gray-700 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 mt-1.5 whitespace-pre-wrap">
              {q.message}
            </p>
            {/* ⚠️ APPROVING AN OFF-ALLOWLIST CHAT LOOKS LIKE SENDING AND IS
                NOT. The draft is written, `serveQueued` refuses it, the row
                leaves the queue and nothing arrives — with nothing to say so.
                All four leads queued on the first run were in exactly this
                state. Say it before the click, not after. */}
            {/* The per-row warning is now the group's, said once above. The
                button below still says « Approuver quand même », so an
                individual unreachable row is never mistaken for a normal one. */}
            {/* Older than 48 h: the worker drops it on approval, nothing is sent. */}
            {q.stale && (
              <p className="text-[11px] text-amber-800 mt-1.5">{t(QUEUE_TEXT.rowStale)}</p>
            )}
            <div className="flex gap-2 mt-2 items-center">
              <Button size="sm" loading={decide.isPending}
                onClick={() => decide.mutate({ id: q.id, ok: true })}>
                {q.stale ? t(QUEUE_TEXT.rowDrop)
                  : q.reachable === false ? t('Approuver quand même') : t('Envoyer')}
              </Button>
              <button className="text-[11.5px] text-gray-500 underline cursor-pointer"
                onClick={() => decide.mutate({ id: q.id, ok: false })}>{t('Ne pas envoyer')}</button>
              {q.contact_phone && (
                <span className="text-[10.5px] text-gray-400 ml-auto tabular-nums">{q.contact_phone}</span>
              )}
            </div>
          </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* ── 3 · Arm them, and see what they did ── */}
      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <h3 className="text-[13px] font-semibold text-gray-800">{t('Vos relances')}</h3>
          {/* Full width under the heading on a phone — sharing the row there
              squeezes it to four cramped lines beside two words. */}
          <p className="text-[10.5px] text-gray-400 leading-snug w-full sm:w-auto sm:max-w-md">
            {t('« Ont avancé » = la commande a bougé dans les {d} jours suivant le message. C’est une coïncidence utile, pas une preuve que le message en soit la cause.',
              { d: String(windows.attributionDays) })}
          </p>
        </div>

        {/* ⚠️ A FAILED COUNT MUST NOT READ AS ZERO RESULTS. Silence here would
            tell the owner every rule is dead when the query simply broke. */}
        {rules.data?.statsError && (
          <p className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
            role="alert">
            {t('Les chiffres n’ont pas pu être calculés — ce ne sont pas des zéros réels. ({e})',
              { e: rules.data.statsError })}
          </p>
        )}

        {rules.isLoading && <p className="text-[12.5px] text-gray-400">{t('Chargement…')}</p>}
        {!rules.isLoading && !list.length && (
          <Card className="border-dashed">
            <p className="text-[12.5px] text-gray-500 text-center py-4">
              {t('Aucune relance pour le moment. Écrivez la première ci-dessus.')}
            </p>
          </Card>
        )}
        {list.map((r) => (
          <RuleCard
            key={r.id} rule={r} laneLabels={laneLabels}
            stat={rules.data?.stats?.[r.id] ?? EMPTY_STAT}
            windows={windows}
          />
        ))}

        {/* ── The built-in ladders ──
            Not rules, no switch, no card above — but they ARE relances that
            reach customers. Leaving them unmeasured would mean the busiest
            follow-ups in the system are the ones nobody can see results for.
            The worker sends the list; this only supplies the wording. */}
        {(rules.data?.builtIns ?? []).map(({ bucket, stat }) => {
          const copy = BUILT_IN_COPY[bucket];
          if (!copy) return null;   // a ladder shipped ahead of its label
          return (
            <Card key={bucket} className="mt-3">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-gray-900">{t(copy.name)}</p>
                  <p className="text-[11.5px] text-gray-600 mt-0.5">{t(copy.when)}</p>
                </div>
                <span className="text-[10.5px] text-gray-400 shrink-0 mt-0.5">
                  {t('non modifiable ici')}
                </span>
              </div>
              <RuleStats stat={stat} windows={windows} />
            </Card>
          );
        })}
      </div>
    </div>
  );
}
